import axios from 'axios';
import { MAIN_URL, getApiHeaders } from '../../Configurations/Urls';
import { resolveModuleListConfig } from '../../Configurations/moduleListRegistry';
import toast from 'react-hot-toast';
import useAuthStore from '../../Zustand/Store/useAuthStore';
import { operatorRequiresValue, OPERATORS } from './operators';

/**
 * Dynamic Filter Service for SystemMetadataController
 * Fully aligned with backend API structure
 */

// ============================================================================
// Authentication & Error Handling
// ============================================================================

/**
 * Get authentication headers with token
 */
const getAuthHeaders = () => getApiHeaders();

/**
 * Handle authentication errors (401) - redirect to login
 */
const handleAuthError = () => {
  toast.error('Session Expired!');
  useAuthStore.getState().logout?.();
  window.location.href = '/login';
};

/**
 * Extract error message from axios error response
 * @param {Object} error - Axios error object
 * @param {string} defaultMessage - Fallback message
 */
const extractErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (!error.response) {
    return `Network error: Cannot connect to backend at ${MAIN_URL}`;
  }

  const { status, data } = error.response;
  return data?.error || data?.details || data?.message || `${defaultMessage} (Status: ${status})`;
};

// ============================================================================
// Organization Context Helper
// ============================================================================

/**
 * Get current organization ID from auth store
 */
const getOrganizationId = () => {
  try {
    const authStore = useAuthStore.getState();
    return authStore?.userData?.organization?.organization_id || null;
  } catch (e) {
    console.warn('Could not access auth store for organization ID:', e);
    return null;
  }
};

// ============================================================================
// FK id/label columns from metadata (system_filter_metadata)
// ============================================================================

/**
 * Resolve the physical FK id column for metadata / lookup options.
 * Uses fk_key_column and column_name from API metadata only.
 */
export const resolveMetadataFkValueField = (fieldKey, meta = {}) => {
  const fkKey = meta.fk_key_column;
  if (fkKey && /_id$/i.test(String(fkKey))) {
    return fkKey;
  }
  const columnName = meta.column_name;
  if (columnName && /_id$/i.test(String(columnName))) {
    return columnName;
  }
  if (fieldKey && /_id$/i.test(String(fieldKey))) {
    return fieldKey;
  }
  return columnName || fieldKey || null;
};

/** Label column from metadata; null lets response parsing auto-detect. */
export const resolveMetadataFkLabelField = (_fieldKey, meta = {}) => {
  const label = meta.fk_label_column;
  return label && String(label).trim() !== '' ? label : null;
};

// ============================================================================
// In-memory cache for lookup options (cleared on full page reload)
// ============================================================================

const _lookupCache = new Map();

// ============================================================================
// Lookup URL Processing (for Foreign Key fields)
// ============================================================================

/** Legacy metadata endpoints that do not match Laravel route prefixes. */
const LOOKUP_URL_ALIASES = {
  '/api/application-users': '/api/application/user',
  '/api/application-user-roles': '/api/application/userrole',
};

const normalizeLookupApiPath = (apiUrl) => {
  if (!apiUrl) return apiUrl;
  const pathOnly = apiUrl.split('?')[0];
  const alias = LOOKUP_URL_ALIASES[pathOnly];
  if (!alias) return apiUrl;
  const query = apiUrl.includes('?') ? apiUrl.slice(apiUrl.indexOf('?')) : '';
  return `${alias}${query}`;
};

/**
 * Process lookup API URL with organization context and query params
 * @param {string} apiUrl - Raw API URL from metadata (ui_options_api)
 */
const processLookupUrl = (apiUrl, contextParams = {}) => {
  if (!apiUrl) return null;

  const orgId = getOrganizationId();
  let processedUrl = normalizeLookupApiPath(apiUrl);

  // Replace {org_id} placeholder if present
  if (orgId) {
    if (apiUrl.includes('{org_id}')) {
      processedUrl = apiUrl.replace(/{org_id}/g, orgId);
    } else if (apiUrl.includes('/organizations/') && !apiUrl.match(/\/organizations\/\d+/)) {
      processedUrl = apiUrl.replace('/organizations/', `/organizations/${orgId}/`);
    }
  }

  // Build full URL if not already absolute
  const fullUrl = processedUrl.startsWith('http')
    ? processedUrl
    : `${MAIN_URL}${processedUrl.startsWith('/') ? processedUrl : '/' + processedUrl}`;

  // Add per_page=all and organization_id when not already present
  try {
    const urlObj = new URL(fullUrl);
    if (!urlObj.searchParams.has('per_page')) {
      urlObj.searchParams.set('per_page', 'all');
    }
    if (orgId && !urlObj.searchParams.has('organization_id')) {
      urlObj.searchParams.set('organization_id', String(orgId));
    }
    // Project-management list APIs: mode=1 omits PKs; filter dropdowns need mode=3 (id + label).
    if (urlObj.pathname.includes('/project-management/')) {
      const mode = urlObj.searchParams.get('mode');
      if (!mode || mode === '1') {
        urlObj.searchParams.set('mode', '3');
      }
    }
    if (contextParams && typeof contextParams === 'object') {
      Object.entries(contextParams).forEach(([key, val]) => {
        if (val != null && val !== '' && !urlObj.searchParams.has(key)) {
          urlObj.searchParams.set(key, String(val));
        }
      });
    }
    return urlObj.toString();
  } catch (e) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    const orgParam = orgId && !fullUrl.includes('organization_id=') ? `&organization_id=${orgId}` : '';
    return `${fullUrl}${separator}per_page=all${orgParam}`;
  }
};

// ============================================================================
// Response Data Extraction (Dynamic)
// ============================================================================

/**
 * Dynamically extract array data from various API response formats
 * Handles: paginated, nested, direct arrays, and keyed responses
 */
const extractResponseArray = (responseData) => {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;

  // Handle standard Laravel pagination format
  if (responseData.data) {
    // Nested pagination: { data: { data: [...] } }
    if (responseData.data?.data && Array.isArray(responseData.data.data)) {
      return responseData.data.data;
    }
    // Simple: { data: [...] }
    if (Array.isArray(responseData.data)) {
      return responseData.data;
    }
  }

  // Search for first array property dynamically
  for (const key of Object.keys(responseData)) {
    const value = responseData[key];
    if (Array.isArray(value)) {
      return value;
    }
    // Handle nested: { SomeKey: { data: [...] } }
    if (value && typeof value === 'object' && Array.isArray(value.data)) {
      return value.data;
    }
  }

  return [];
};

/**
 * Dynamically find the ID field from an item
 * Priority: fields ending with _id > id/value/key > first numeric field
 */
const findIdField = (item) => {
  if (!item || typeof item !== 'object') return null;
  const keys = Object.keys(item);

  // Priority 1: Fields ending with '_id' (prefer more specific, longer names)
  const idFields = keys
    .filter((key) => key.endsWith('_id'))
    .sort((a, b) => b.length - a.length);
  if (idFields.length > 0) return idFields[0];

  // Priority 2: Common ID field names
  const commonFields = ['id', 'value', 'key', 'identifier'];
  const found = commonFields.find((f) => item[f] !== undefined);
  if (found) return found;

  // Priority 3: First numeric-like field
  const numericField = keys.find((key) => {
    const val = item[key];
    return typeof val === 'number' || (typeof val === 'string' && /^\d+$/.test(val));
  });
  return numericField || null;
};

/**
 * Dynamically find the label/display field from an item
 * Priority: name/label/title > fields ending with _name/_label/_title > first string field
 */
const findLabelField = (item) => {
  if (!item || typeof item !== 'object') return null;
  const keys = Object.keys(item);

  // Patterns to exclude (not display labels)
  const excludePatterns = ['_id', 'short', 'mail', 'email', 'phone', 'code', 'abbr', 'password', 'token'];

  // Priority 1: Exact common label field names
  const exactMatches = ['label', 'title', 'name'];
  const exactMatch = exactMatches.find((f) => item[f] !== undefined && typeof item[f] === 'string');
  if (exactMatch) return exactMatch;

  // Priority 2: Fields ending with _name, _label, _title
  const suffixFields = keys
    .filter((key) => {
      const lower = key.toLowerCase();
      return (
        (lower.endsWith('_name') || lower.endsWith('_label') || lower.endsWith('_title')) &&
        !excludePatterns.some((p) => lower.includes(p))
      );
    })
    .sort((a, b) => a.split('_').length - b.split('_').length); // prefer shorter/simpler names
  if (suffixFields.length > 0) return suffixFields[0];

  // Priority 3: Fields containing name/label/title (non-ID)
  const containsFields = keys.filter((key) => {
    const lower = key.toLowerCase();
    return (
      (lower.includes('name') || lower.includes('label') || lower.includes('title')) &&
      !lower.endsWith('_id') &&
      !excludePatterns.some((p) => lower.includes(p)) &&
      typeof item[key] === 'string'
    );
  });
  if (containsFields.length > 0) return containsFields[0];

  // Priority 4: First string field that's not an ID
  const stringField = keys.find((key) => {
    const val = item[key];
    return (
      typeof val === 'string' &&
      val.length > 0 &&
      !key.endsWith('_id') &&
      !excludePatterns.some((p) => key.toLowerCase().includes(p))
    );
  });
  return stringField || null;
};

function formatEmsPersonName(person) {
  if (!person || typeof person !== 'object') return '';
  const fromParts = [person.first_name, person.middle_name, person.last_name]
    .filter((part) => part != null && String(part).trim() !== '')
    .join(' ')
    .trim();
  if (fromParts) return fromParts;
  return String(
    person.person_full_name ?? person.full_name ?? person.name ?? ''
  ).trim();
}

function buildEmsAdmissionLookupLabel(item) {
  if (!item || typeof item !== 'object') return null;
  const admissionNumber = String(item.admission_number ?? '').trim();
  const studentName =
    formatEmsPersonName(item.student) ||
    String(item.student_name ?? '').trim();

  if (admissionNumber && studentName) {
    return `${admissionNumber} — ${studentName}`;
  }
  if (admissionNumber) return admissionNumber;
  if (studentName) return studentName;
  return null;
}

function buildEmsStudentLookupLabel(item) {
  if (!item || typeof item !== 'object') return null;
  const name = formatEmsPersonName(item);
  const code = String(item.student_id ?? '').trim();
  if (name && code) return `${name} (${code})`;
  if (name) return name;
  if (code) return code;
  return null;
}

function formatEmsDueDateLabel(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s || s === '-') return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  return s;
}

function buildEmsFeeInstallmentLookupLabel(item) {
  if (!item || typeof item !== 'object') return null;
  const instNo = item.installment_number ?? item.inst_no;
  const student =
    item.student ??
    item.student_obj ??
    item?.admission?.student ??
    null;
  const studentName =
    formatEmsPersonName(student) || String(item.student_name ?? '').trim();
  const dueDate = formatEmsDueDateLabel(item.due_date);
  const admissionNo = String(
    item?.admission?.admission_number ?? item.admission_number ?? ''
  ).trim();

  const parts = [];
  if (instNo != null && String(instNo).trim() !== '') {
    parts.push(`Inst. ${instNo}`);
  }
  if (studentName) parts.push(studentName);
  else if (admissionNo) parts.push(`Adm ${admissionNo}`);
  if (dueDate) parts.push(`Due ${dueDate}`);

  return parts.length ? parts.join(' — ') : null;
}

function buildEmsBatchLookupLabel(item) {
  if (!item || typeof item !== 'object') return null;
  const code = String(item.batch_code ?? '').trim();
  const name = String(item.batch_name ?? '').trim();
  if (code && name) return `${code} — ${name}`;
  if (code) return code;
  if (name) return name;
  return null;
}

function buildEmsOrganizationUserLookupLabel(item) {
  if (!item || typeof item !== 'object') return null;

  const fullName = String(item.full_name ?? '').trim();
  if (fullName && !/^\d+$/.test(fullName)) return fullName;

  const nameFromParts = formatEmsPersonName(item);
  if (nameFromParts) return nameFromParts;

  const appUser =
    item.application_user ??
    item.applicationUser ??
    (Array.isArray(item.applicationuser) ? item.applicationuser[0] : null) ??
    (Array.isArray(item.Applicationuser) ? item.Applicationuser[0] : null);
  if (appUser && typeof appUser === 'object') {
    const appFullName = String(appUser.full_name ?? '').trim();
    if (appFullName && !/^\d+$/.test(appFullName)) return appFullName;
    const appName = formatEmsPersonName(appUser);
    if (appName) return appName;
  }

  const person =
    item.person ??
    item.organization_person?.Person ??
    item.organizationPerson?.Person ??
    item.organization_person?.person ??
    null;
  const personName = formatEmsPersonName(person);
  if (personName) return personName;

  const personCode = String(item.person_code ?? '').trim();
  if (personCode && !/^\d+$/.test(personCode)) return personCode;

  const userType = String(item.user_type_name ?? '').trim();
  const userId = item.organization_user_id ?? item.trainer_employee_id ?? null;
  if (userType && userId != null && String(userId).trim() !== '') {
    return `${userType} #${userId}`;
  }

  return null;
}

function isEmsAdmissionsLookupUrl(apiUrl, labelField) {
  const url = String(apiUrl || '').toLowerCase();
  return (
    url.includes('/api/ems/admissions') || labelField === 'admission_number'
  );
}

function isEmsStudentsLookupUrl(apiUrl, labelField) {
  const url = String(apiUrl || '').toLowerCase();
  return url.includes('/api/ems/students') || labelField === 'first_name';
}

function isEmsFeeInstallmentsLookupUrl(apiUrl, labelField) {
  const url = String(apiUrl || '').toLowerCase();
  return (
    url.includes('/api/ems/fee-installments') ||
    labelField === 'installment_number'
  );
}

function isEmsBatchesLookupUrl(apiUrl, labelField) {
  const url = String(apiUrl || '').toLowerCase();
  return (
    url.includes('/api/ems/batches') ||
    labelField === 'batch_name' ||
    labelField === 'batch_code'
  );
}

function isEmsOrganizationUsersLookupUrl(apiUrl, labelField) {
  const url = String(apiUrl || '').toLowerCase();
  return (
    url.includes('/users-data-all') ||
    labelField === 'full_name' ||
    labelField === 'person_code'
  );
}

function resolveEmsLookupLabel(item, lookupUrl, labelField, resolvedValue) {
  if (isEmsAdmissionsLookupUrl(lookupUrl, labelField)) {
    const composite = buildEmsAdmissionLookupLabel(item);
    if (composite) return composite;
  }
  if (isEmsStudentsLookupUrl(lookupUrl, labelField)) {
    const composite = buildEmsStudentLookupLabel(item);
    if (composite) return composite;
  }
  if (isEmsFeeInstallmentsLookupUrl(lookupUrl, labelField)) {
    const composite = buildEmsFeeInstallmentLookupLabel(item);
    if (composite) return composite;
  }
  if (isEmsBatchesLookupUrl(lookupUrl, labelField)) {
    const composite = buildEmsBatchLookupLabel(item);
    if (composite) return composite;
  }
  if (isEmsOrganizationUsersLookupUrl(lookupUrl, labelField)) {
    const composite = buildEmsOrganizationUserLookupLabel(item);
    if (composite) return composite;
  }
  return null;
}

/**
 * Whether a filter value is safe to send to the API / store in the URL.
 */
export const isValidFilterValue = (value) => {
  if (value == null || value === '' || value === 'undefined') return false;
  if (Array.isArray(value)) {
    const cleaned = value.filter(
      (v) =>
        v != null &&
        v !== '' &&
        v !== 'undefined' &&
        String(v).trim() !== ''
    );
    return cleaned.length > 0;
  }
  return String(value).trim() !== '' && String(value) !== 'undefined';
};

/**
 * Normalize filter values for API (drop invalid entries from arrays).
 */
export const normalizeFilterValueForApi = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'string' ? v.trim() : v))
      .filter(
        (v) =>
          v != null &&
          v !== '' &&
          v !== 'undefined' &&
          String(v).trim() !== ''
      );
  }
  return value;
};

/**
 * Transform any item to { value, label } format for dropdowns
 */
const transformToOption = (item) => {
  if (!item || typeof item !== 'object') {
    return { value: item, label: String(item) };
  }

  // Already in correct format
  if (item.value !== undefined && item.label !== undefined) {
    return { value: item.value, label: String(item.label) };
  }

  const idFieldName = findIdField(item);
  const labelFieldName = findLabelField(item);
  const value = idFieldName !== null ? item[idFieldName] : Object.values(item)[0];
  let label = labelFieldName ? item[labelFieldName] : null;

  // Fallback: use first string value different from value
  if (!label) {
    const firstString = Object.values(item).find(
      (v) => typeof v === 'string' && v !== String(value)
    );
    label = firstString || String(value);
  }

  return { value, label: String(label || value) };
};

// ============================================================================
// Core API Methods
// ============================================================================

/**
 * GET /api/metadata/{module}
 * Fetch filter metadata (filterable fields) for a module
 *
 * Response format from backend:
 * {
 *   message: string,
 *   module: string,
 *   fields: [{
 *     field_key, label, field_type, table_name, column_name,
 *     allowed_operators, default_operator, ui_type, is_filterable,
 *     filter_order, fk_table, fk_key_column, fk_label_column,
 *     join_alias, ui_options_api, enum_options
 *   }]
 * }
 */
const getFilterMetadata = async (module) => {
  try {
    const response = await axios.get(`${MAIN_URL}/api/metadata/${module}`, {
      headers: getAuthHeaders(),
    });
    // Backend may return either:
    // - { fields: [...] } (older format)
    // - { popular_filters: [...], groups: [{ fields: [...] }] } (newer format)
    const fieldsFromGroups = Array.isArray(response.data?.groups)
      ? response.data.groups.flatMap((g) => (Array.isArray(g?.fields) ? g.fields : []))
      : [];
    const fieldsFromPopular = Array.isArray(response.data?.popular_filters)
      ? response.data.popular_filters
      : [];
    const mergedFields = Array.isArray(response.data?.fields)
      ? response.data.fields
      : [...fieldsFromPopular, ...fieldsFromGroups];

    // Deduplicate by field_key
    const byKey = new Map();
    mergedFields.forEach((f) => {
      const key = f?.field_key;
      if (!key) return;
      if (!byKey.has(key)) byKey.set(key, f);
    });
    const finalFields = Array.from(byKey.values());

    if (finalFields.length > 0) {
      const moduleName = response.data.module || module;
      const apiModuleConfig = response.data.module_config || null;
      return {
        success: true,
        fields: finalFields,
        popular_filters: response.data.popular_filters || [],
        groups: response.data.groups || [],
        module: moduleName,
        module_info: response.data.module_info || null,
        module_config: apiModuleConfig,
        list_config: resolveModuleListConfig(moduleName, null, apiModuleConfig),
        message: response.data.message,
      };
    }

    return {
      success: false,
      fields: [],
      popular_filters: [],
      groups: [],
      error: 'Invalid response format - fields array not found',
    };
  } catch (error) {
    console.error('Error fetching filter metadata:', error);

    if (error.response?.status === 401) {
      handleAuthError();
      return { success: false, fields: [], popular_filters: [], groups: [], error: 'Unauthorized - Please login again' };
    }

    if (error.response?.status === 404) {
      // Some pages use dedicated "*_intern" modules. If metadata isn't configured yet,
      // fall back to the base module so the filter UI remains usable.
      const mod = String(module || '');
      if (/_intern$/i.test(mod)) {
        const baseModule = mod.replace(/_intern$/i, '');
        try {
          const fallback = await axios.get(`${MAIN_URL}/api/metadata/${baseModule}`, {
            headers: getAuthHeaders(),
          });

          const fieldsFromGroups = Array.isArray(fallback.data?.groups)
            ? fallback.data.groups.flatMap((g) => (Array.isArray(g?.fields) ? g.fields : []))
            : [];
          const fieldsFromPopular = Array.isArray(fallback.data?.popular_filters)
            ? fallback.data.popular_filters
            : [];
          const mergedFields = Array.isArray(fallback.data?.fields)
            ? fallback.data.fields
            : [...fieldsFromPopular, ...fieldsFromGroups];

          const byKey = new Map();
          mergedFields.forEach((f) => {
            const key = f?.field_key;
            if (!key) return;
            if (!byKey.has(key)) byKey.set(key, f);
          });

          const finalFields = Array.from(byKey.values());
          if (finalFields.length > 0) {
            return {
              success: true,
              fields: finalFields,
              popular_filters: fallback.data.popular_filters || [],
              groups: fallback.data.groups || [],
              module: fallback.data.module || baseModule,
              message: fallback.data.message,
              // optional hint for debugging in dev
              usedFallbackModule: baseModule,
            };
          }
        } catch (_) {
          // fall through to standard 404 error below
        }
      }

      return {
        success: false,
        fields: [],
        popular_filters: [],
        groups: [],
        error: `Module "${module}" not found. Ensure metadata is configured for this module.`,
      };
    }

    return {
      success: false,
      fields: [],
      popular_filters: [],
      groups: [],
      error: extractErrorMessage(error, 'Failed to fetch filter metadata'),
    };
  }
};

/**
 * POST /api/metadata/{module}
 * Get filtered data based on metadata-driven filters
 *
 * @param {string} module - Module name or module_id
 * @param {Object} filterParams - Filter parameters
 * @param {Array} filterParams.filters - [{field, operator, value}]
 * @param {Object} filterParams.sort - {field, direction}
 * @param {number} filterParams.page - Page number (default: 1)
 * @param {number} filterParams.limit - Items per page (default: 25)
 * @param {number} filterParams.organization_id - Organization ID (optional, auto-detected)
 */
const getFilteredData = async (module, filterParams = {}) => {
  try {
    const {
      filters = [],
      sort = null,
      page = 1,
      limit = 25,
      organization_id = null,
    } = filterParams;

    // Auto-detect organization ID if not provided
    const orgId = organization_id || getOrganizationId();

    // Ensure filters have proper format - set value to null for operators that don't require values
    const normalizedFilters = filters.map(filter => {
      const requiresValue = operatorRequiresValue(filter.operator);
      
      return {
        field: filter.field,
        operator: filter.operator,
        value: requiresValue ? (filter.value !== undefined ? filter.value : null) : null,
      };
    });

    const requestBody = {
      module_id: module,
      module,
      filters: normalizedFilters,
      page,
      limit,
      ...(orgId && { organization_id: orgId }),
      ...(sort && { sort }),
    };

    const response = await axios.post(
      `${MAIN_URL}/api/metadata/${module}`,
      requestBody,
      { headers: getAuthHeaders() }
    );

    if (response.data?.success !== false) {
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination || {
          page,
          limit,
          total: 0,
          totalPages: 0,
          hasPrev: false,
          hasNext: false,
        },
        message: response.data.message,
      };
    }

    return {
      success: false,
      data: [],
      pagination: null,
      error: response.data.error || 'Failed to fetch data',
    };
  } catch (error) {
    console.error('Error fetching filtered data:', error);

    if (error.response?.status === 401) {
      handleAuthError();
      return { success: false, data: [], pagination: null, error: 'Unauthorized' };
    }

    if (error.response?.status === 400) {
      // Validation or filter errors from backend
      const errorData = error.response.data;
      const message = errorData.message || errorData.error || 'Invalid filter parameters';
      toast.error(message);
      return {
        success: false,
        data: [],
        pagination: null,
        error: message,
        invalidFields: errorData.invalid_fields || [],
        invalidOperators: errorData.invalid_operators || [],
      };
    }

    if (error.response?.status === 422) {
      const validationErrors = error.response.data.errors || {};
      const errorMessages = Object.values(validationErrors).flat().join(', ');
      toast.error(errorMessages || 'Validation failed');
      return {
        success: false,
        data: [],
        pagination: null,
        error: errorMessages || 'Validation failed',
      };
    }

    const errorMessage = extractErrorMessage(error, 'Failed to fetch filtered data');
    toast.error(errorMessage);
    return {
      success: false,
      data: [],
      pagination: null,
      error: errorMessage,
    };
  }
};

/**
 * Fetch lookup options for foreign key fields
 * Uses ui_options_api from metadata
 *
 * @param {string} apiUrl - API URL from metadata (ui_options_api field)
 * @param {Object} options - Additional options
 * @param {string} options.valueField - Override value field name
 * @param {string} options.labelField - Override label field name
 */
const fetchLookupOptions = async (apiUrl, options = {}) => {
  if (!apiUrl) {
    console.warn('fetchLookupOptions called without API URL');
    return [];
  }

  try {
    const finalUrl = processLookupUrl(apiUrl, options.contextParams || {});

    // Return cached result if available (avoids re-fetching every time the
    // filter dialog opens — cache key includes the full resolved URL).
    const cacheKey = finalUrl;
    if (_lookupCache.has(cacheKey)) {
      return _lookupCache.get(cacheKey);
    }

    const fetchOptionsFromUrl = async (url) => {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return extractResponseArray(response.data);
    };

    let data = await fetchOptionsFromUrl(finalUrl);

    // mode=1 responses have labels but no ids — upgrade to mode=3 when options would be empty.
    if ((!Array.isArray(data) || data.length === 0) && apiUrl.includes('mode=1')) {
      const mode3Url = processLookupUrl(
        apiUrl.replace(/([?&])mode=1(&|$)/, '$1mode=3$2'),
        options.contextParams || {}
      );
      if (mode3Url && mode3Url !== finalUrl) {
        data = await fetchOptionsFromUrl(mode3Url);
      }
    }

    if (!Array.isArray(data) || data.length === 0) {
      console.warn('No data found in lookup response:', apiUrl);
      return [];
    }


    const isNumericId = (v) =>
      typeof v === 'number' ||
      (typeof v === 'string' && /^\d+$/.test(String(v).trim()));

    const resolveFkValue = (item, valueField) => {
      if (valueField && item[valueField] !== undefined && item[valueField] !== null) {
        const candidate = item[valueField];
        // column_name may be a display alias (e.g. "project") — only accept numeric FK ids
        if (isNumericId(candidate)) return candidate;
      }
      // When the explicit valueField is absent (e.g. mode=1 strips the PK), fall back to
      // the longest _id field in the item — longer names are more entity-specific and less
      // likely to be an unrelated FK (e.g. prefer organization_project_task_id over organization_project_id).
      const idFields = Object.keys(item)
        .filter(k => k.endsWith('_id') && item[k] !== undefined && item[k] !== null && isNumericId(item[k]))
        .sort((a, b) => b.length - a.length);
      if (idFields.length > 0) return item[idFields[0]];
      for (const key of ['id', 'value', 'key', 'identifier']) {
        if (item[key] !== undefined && item[key] !== null) return item[key];
      }
      return null;
    };

    const isUsableLookupLabel = (str, resolvedValue) => {
      const trimmed = String(str ?? '').trim();
      if (!trimmed) return false;
      if (resolvedValue != null && trimmed === String(resolvedValue)) return false;
      return true;
    };

    const resolveFkLabel = (item, labelField, resolvedValue) => {
      const candidateFields = [];
      if (labelField) candidateFields.push(labelField);
      [
        'full_name',
        'name',
        'user_name',
        'user_role_name',
        'role_name',
        'email',
        'phone_number',
      ].forEach((field) => {
        if (!candidateFields.includes(field)) candidateFields.push(field);
      });

      for (const field of candidateFields) {
        const val = item[field];
        if (val === undefined || val === null) continue;
        const str = String(val).trim();
        if (!isUsableLookupLabel(str, resolvedValue)) continue;
        return str;
      }

      const autoKey = findLabelField(item);
      if (autoKey) {
        const autoVal = item[autoKey];
        const str = String(autoVal ?? '').trim();
        if (isUsableLookupLabel(str, resolvedValue) && !/^\d+$/.test(str)) {
          return str;
        }
      }
      return null;
    };

    const resolveLookupLabel = (item, resolvedValue) => {
      const emsLabel = resolveEmsLookupLabel(
        item,
        apiUrl,
        options.labelField,
        resolvedValue
      );
      if (emsLabel) return emsLabel;

      let label = resolveFkLabel(item, options.labelField, resolvedValue);
      if (!label && resolvedValue !== null) {
        const autoKey = findLabelField(item);
        if (autoKey) {
          const autoVal = item[autoKey];
          if (
            autoVal !== null &&
            autoVal !== undefined &&
            String(autoVal).trim() !== '' &&
            !/^\d+$/.test(String(autoVal).trim())
          ) {
            label = String(autoVal);
          }
        }
      }
      return label;
    };

    // Transform to {value, label} format
    let result = data
      .map((item) => {
        if (options.valueField || options.labelField) {
          const value = resolveFkValue(item, options.valueField);
          const label = resolveLookupLabel(item, value);
          if (value !== undefined && value !== null) {
            const displayLabel =
              label && !/^\d+$/.test(String(label).trim())
                ? String(label).trim()
                : `User #${value}`;
            return { value, label: displayLabel };
          }
          return null;
        }
        return transformToOption(item);
      })
      .filter(Boolean);

    // When mode=3 returns IDs but no labels (all labels are numeric), try fetching
    // labels from a companion request. mode=1 has the label field but strips the PK —
    // merge is only safe when both return the exact same record count (same filters,
    // same sort). Falls back to stripping the mode param entirely (default endpoint).
    const hasRealLabels = result.some(r => r.label && !/^\d+$/.test(String(r.label)));
    if (!hasRealLabels && result.length > 0 && finalUrl.includes('mode=3')) {
      // Add per_page=all to ensure counts match between mode=3 and mode=1 fetches.
      const withAllPages = (url) =>
        url.includes('per_page=') ? url : url + (url.includes('?') ? '&' : '?') + 'per_page=all';

      const tryMergeLabels = async (labelUrl) => {
        const fullLabelUrl = withAllPages(labelUrl);
        const labelRes = await axios.get(fullLabelUrl, { headers: getAuthHeaders() });
        const labelData = extractResponseArray(labelRes.data);
        if (!Array.isArray(labelData) || labelData.length === 0) return false;
        // When counts match, merge by position (same sort = same order guaranteed).
        // When they differ, try to enrich only the IDs we have by partial match on index.
        const sourceCount = labelData.length;
        const targetCount = result.length;
        const canMergeByPosition = sourceCount === targetCount;
        const merged = result.map((opt, i) => {
          const srcIndex = canMergeByPosition ? i : Math.round((i / targetCount) * (sourceCount - 1));
          const lbl = resolveLookupLabel(labelData[srcIndex], opt.value);
          return lbl ? { ...opt, label: lbl } : opt;
        });
        if (!merged.some(r => r.label && !/^\d+$/.test(String(r.label)))) return false;
        result = merged;
        return true;
      };

      try {
        // Attempt 1: same URL with mode=1 (has labels, no PK); ensure all pages
        const mode1Url = withAllPages(finalUrl.replace('mode=3', 'mode=1'));
        const mode3AllUrl = withAllPages(finalUrl);
        // Re-fetch mode=3 with per_page=all in case original was paginated
        if (!finalUrl.includes('per_page=')) {
          const freshRes = await axios.get(mode3AllUrl, { headers: getAuthHeaders() });
          const freshData = extractResponseArray(freshRes.data);
          if (Array.isArray(freshData) && freshData.length > 0) {
            result = freshData
              .map((item) => {
                const value = resolveFkValue(item, options.valueField);
                const label = resolveLookupLabel(item, value);
                return value !== undefined && value !== null
                  ? { value, label: label ?? String(value) }
                  : null;
              })
              .filter(Boolean);
          }
        }
        const didMerge = await tryMergeLabels(mode1Url);
        // Attempt 2: drop mode param entirely — some backends return full data by default
        if (!didMerge) {
          const noModeUrl = finalUrl
            .replace(/([?&])mode=3(&|$)/, (_, prefix, suffix) => suffix ? prefix : '')
            .replace(/\?$/, '');
          await tryMergeLabels(noModeUrl);
        }
      } catch (_) {
        // use result as-is — backend fix needed: mode=3 should include label fields
      }
    }

    // Deduplicate by value (same record returned multiple times via JOIN paths)
    const seenValues = new Set();
    result = result.filter((r) => {
      const key = String(r.value);
      if (seenValues.has(key)) return false;
      seenValues.add(key);
      return true;
    });

    // Merge duplicate labels into a single option.
    // UX goal: users see one entry per name, not repeated entries with (#ID).
    // Semantics: merged option stores all ids as comma-separated string, so the filter
    // can match all tasks that share the same label.
    const byLabel = new Map();
    for (const opt of result) {
      const labelKey = String(opt.label ?? '').trim();
      // Keep id-only rows (e.g. mode=3 rows before label merge) instead of dropping them.
      if (!labelKey) {
        byLabel.set(`__id__${opt.value}`, {
          label: String(opt.value),
          values: new Set([String(opt.value)]),
        });
        continue;
      }
      const existing = byLabel.get(labelKey);
      const nextVal = String(opt.value);
      if (!existing) {
        byLabel.set(labelKey, { label: labelKey, values: new Set([nextVal]) });
      } else {
        existing.values.add(nextVal);
      }
    }
    result = Array.from(byLabel.values()).map(({ label, values }) => {
      const arr = Array.from(values);
      // stable ordering: numeric ids ascending, otherwise string
      arr.sort((a, b) => {
        const an = /^\d+$/.test(a) ? Number(a) : NaN;
        const bn = /^\d+$/.test(b) ? Number(b) : NaN;
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return a.localeCompare(b);
      });
      return {
        label,
        value: arr.length === 1 ? arr[0] : arr.join(','),
      };
    });

    if (result.length > 0) {
      _lookupCache.set(cacheKey, result);
    }
    return result;
  } catch (error) {
    console.error('Error fetching lookup options:', error, 'URL:', apiUrl);

    if (error.response?.status === 401) {
      handleAuthError();
    }

    return [];
  }
};

/**
 * Get enum options from metadata field
 * Returns pre-formatted array from backend
 *
 * @param {Array} enumOptions - enum_options from metadata field
 */
const getEnumOptions = (enumOptions) => {
  if (!enumOptions || !Array.isArray(enumOptions)) {
    return [];
  }

  return enumOptions.map((option) => {
    // Already in {value, label} format from backend
    if (option.value !== undefined && option.label !== undefined) {
      return option;
    }
    // Simple string array
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    return transformToOption(option);
  });
};

/**
 * Build filter options for a field based on its type
 * Automatically fetches lookup options for foreign_key and enum fields
 *

 */
const getFieldOptions = async (field) => {
  if (!field) return [];

  const fieldType = field.field_type || field.ui_type;

  // Enum / boolean — options from metadata enum_options column
  if ((fieldType === 'enum' || fieldType === 'boolean') && field.enum_options) {
    return getEnumOptions(field.enum_options);
  }

  // Foreign key field - fetch from API (ui_options_api from lookup_api_endpoint in DB)
  if (fieldType === 'foreign_key' && field.ui_options_api) {
    return fetchLookupOptions(field.ui_options_api, {
      valueField: resolveMetadataFkValueField(field.field_key, field),
      labelField: resolveMetadataFkLabelField(field.field_key, field),
      contextParams: field.lookupContextParams,
    });
  }

  return [];
};

/**
 * Build a complete filter configuration from metadata
 * Returns fields with their options pre-loaded
 *
 * @param {string} module - Module name
 */
const buildFilterConfig = async (module) => {
  const metadata = await getFilterMetadata(module);

  if (!metadata.success) {
    return metadata;
  }

  // Enrich fields with options
  const enrichedFields = await Promise.all(
    metadata.fields.map(async (field) => {
      const options = await getFieldOptions(field);
      return {
        ...field,
        options: options.length > 0 ? options : undefined,
      };
    })
  );

  return {
    success: true,
    module: metadata.module,
    fields: enrichedFields,
  };
};

// ============================================================================
// Export
// ============================================================================

const FilterService = {
  // Core API methods
  getFilterMetadata,
  getFilteredData,
  fetchLookupOptions,

  // Helper methods
  getEnumOptions,
  getFieldOptions,
  buildFilterConfig,

  // Utility methods (for advanced usage)
  processLookupUrl,
  extractResponseArray,
  transformToOption,
  getOrganizationId,
  resolveMetadataFkValueField,
  resolveMetadataFkLabelField,
  resolveModuleListConfig,
};

/** Clear FK lookup cache (e.g. on logout or organization switch). */
export const clearLookupOptionsCache = () => {
  _lookupCache.clear();
};

export default FilterService;
